import React, { useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import { createMenuSlideApi } from '@features/digital-signage/menu-slide/api/menu-slide.api';
import { createSitesApi } from '@features/sites/api/sites.api';
import { useSitesByOrgQuery } from '@features/sites/queries/sites.queries';
import { auth } from '@core/utils/firebase/firebase';

import { MenuTable } from '../menu-table/menu-table';
import { useSiteMealPlansQuery } from '../queries/menus.queries';
import { useSyncAllMenusMutation } from '../queries/menus.mutations';

interface OrganizationMenusPageProps {
  organizationId: number;
}

function OrganizationMenusPage({
  organizationId,
}: OrganizationMenusPageProps) {
  const [search, setSearch] = useState('');
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [propertyId, setPropertyId] = useState('');
  const [cycleStartDate, setCycleStartDate] = useState('');
  const [showOnSignage, setShowOnSignage] = useState(false);
  const [displayDuration, setDisplayDuration] = useState(15);
  const [addError, setAddError] = useState<string | null>(null);

  const { mutateAsync: syncAllMenus, isPending: isSyncing } = useSyncAllMenusMutation();

  const { data: allSites = [], isLoading: sitesLoading } = useSitesByOrgQuery({
    organizationId,
    enabled: Boolean(organizationId),
  });

  const { data: menusData } = useSiteMealPlansQuery({
    organizationId,
    page: 0,
    rowsPerPage: 1000,
    sortField: 'cycleStartDate',
    sortOrder: 'desc',
    enabled: Boolean(organizationId),
  });

  const menuCount = menusData?.totalCount ?? menusData?.data?.length ?? 0;

  const sitesWithMenus = new Set(
    menusData?.data?.map((menu) => menu.siteId) ?? [],
  );
  const availableSites = allSites.filter(
    (site) => !sitesWithMenus.has(site.id),
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const handleAddMenuClick = () => {
    setSelectedSiteId('');
    setPropertyId('');
    setCycleStartDate('');
    setShowOnSignage(false);
    setDisplayDuration(15);
    setAddError(null);
    setShowAddMenu(true);
  };

  const handleCloseAddMenu = () => {
    setShowAddMenu(false);
    setSelectedSiteId('');
    setPropertyId('');
    setCycleStartDate('');
    setShowOnSignage(false);
    setDisplayDuration(15);
    setAddError(null);
  };

  const handleAddMenuConfirm = async () => {
    if (!selectedSiteId) {
      setAddError('Please select a site.');
      return;
    }

    if (!propertyId || !cycleStartDate) {
      setAddError('Property ID and cycle start date are required.');
      return;
    }

    const parsedPropertyId = parseInt(propertyId, 10);
    if (Number.isNaN(parsedPropertyId)) {
      setAddError('Property ID must be a number.');
      return;
    }

    const parsedSiteId = parseInt(selectedSiteId, 10);

    try {
      setAddError(null);

      await syncAllMenus({
        siteId: parsedSiteId,
        propertyId: parsedPropertyId,
        cycleStartDate,
      });

      const sitesApi = createSitesApi(auth as any);
      const menuSlideApi = createMenuSlideApi(auth as any);

      await sitesApi.updateSite(parsedSiteId, {
        showMenu: showOnSignage,
      });

      if (showOnSignage && displayDuration > 0) {
        try {
          const existingSlide = await menuSlideApi.getMenuSlideBySiteId(
            parsedSiteId,
          );
          if (existingSlide) {
            await menuSlideApi.updateMenuSlide(existingSlide.id, {
              displayDuration,
            });
          } else {
            await menuSlideApi.createMenuSlide({
              siteId: parsedSiteId,
              displayDuration,
            });
          }
        } catch (slideErr) {
          console.error('Error managing menu slide:', slideErr);
        }
      }

      handleCloseAddMenu();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Unable to add menu.';
      setAddError(message);
    }
  };

  return (
    <Box width="100%">
      <Paper elevation={0} sx={{ p: 0 }}>
        <Box display="flex" flexDirection="column" gap={2} p={3}>
          <Box
            display="flex"
            flexDirection={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', sm: 'center' }}
            gap={2}
          >
            <Typography variant="h6" fontWeight={600}>
              Menus ({menuCount})
            </Typography>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              gap={2}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddMenuClick}
              >
                Add New Menu
              </Button>
              <TextField
                placeholder="Search"
                size="small"
                value={search}
                onChange={handleSearchChange}
                sx={{ minWidth: 250 }}
              />
            </Stack>
          </Box>
          <MenuTable
            organizationId={organizationId}
            showSearch={false}
            search={search}
            onSearchChange={handleSearchChange}
          />
        </Box>
      </Paper>

      <Dialog
        open={showAddMenu}
        onClose={handleCloseAddMenu}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Menu</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <FormControl
              fullWidth
              required
              disabled={sitesLoading || availableSites.length === 0}
            >
              <InputLabel>Site</InputLabel>
              <Select
                value={selectedSiteId}
                label="Site"
                onChange={(e) => setSelectedSiteId(e.target.value)}
              >
                {availableSites.length === 0 ? (
                  <MenuItem disabled value="">
                    {sitesLoading ? 'Loading sites...' : 'No available sites'}
                  </MenuItem>
                ) : (
                  availableSites.map((site) => (
                    <MenuItem key={site.id} value={site.id.toString()}>
                      {site.name} (ID: {site.id})
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
            <TextField
              label="Property ID"
              type="number"
              value={propertyId}
              onChange={(e) => setPropertyId(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Cycle Start Date"
              type="date"
              value={cycleStartDate}
              onChange={(e) => setCycleStartDate(e.target.value)}
              fullWidth
              required
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={showOnSignage}
                  onChange={(e) => setShowOnSignage(e.target.checked)}
                />
              }
              label="Display on Digital Signage"
            />
            {showOnSignage && (
              <TextField
                label="Display Duration (seconds)"
                type="number"
                value={displayDuration}
                onChange={(e) =>
                  setDisplayDuration(
                    Math.max(1, parseInt(e.target.value) || 15),
                  )
                }
                fullWidth
                required
                slotProps={{ htmlInput: { min: 1 } }}
              />
            )}
            {addError && <Alert severity="error">{addError}</Alert>}
            <Typography variant="body2" color="text.secondary">
              The menu will be synced for the selected site.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddMenu}>Cancel</Button>
          <Button
            onClick={handleAddMenuConfirm}
            color="primary"
            variant="contained"
            disabled={
              isSyncing ||
              !selectedSiteId ||
              !propertyId ||
              !cycleStartDate ||
              (showOnSignage && displayDuration < 1)
            }
          >
            {isSyncing ? 'Adding…' : 'Add Menu'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export { OrganizationMenusPage, type OrganizationMenusPageProps };
